import {UIGameCardController} from './ui_game_card/UIGameCardController'
import {UIGamePlayersController} from './ui_game_players/UIGamePlayersController'
import {UIGameTradeCreationController} from '../trade/ui_trade_creation/UIGameTradeCreationController'
import {UIGameFeedController} from './ui_game_feed/UIGameFeedController'
import {UIGameCanvasController} from './UIGameCanvasController'
import {UIGameMapController} from './ui_game_map/UIGameMapController'
import {UIGameBankController} from './UIGameBankController'
import {UIGameActionsController} from './ui_game_actions/UIGameActionsController'
import {UIGameTimerController} from './UIGameTimerController'
import {UIGameToolTipController} from './ui_game_tooltip/UIGameToolTipController'
import {UIGameVoteController} from './ui_game_vote/UIGameVoteController'
import {UIGameEventController} from './UIGameEventController'
import {GameClientState} from '../../../../shared/GameClientState'
import {BuildGameConfigurations, ModeSettingEnum} from '@colonist/game-base-types'
import {SocketGameSend} from '../socket/SocketGameSend'
import {UIAdController} from '../ui_ad/UIAdController'
import {socket} from '../EntryShared'
import {UIGamePauseController} from './UIGamePauseController'
import {UIGameSettingsController, UIGameSettingsMethods} from './UIGameSettingsController'
import {UIGameLayout} from './UIGameLayout'
import {CA} from '../analytics/Analytics'
import {UIGameController} from './UIGameController'
import {UIGameDiceController} from './ui_game_dice/UIGameDiceController'
import {GameStateEnums} from '../../../../shared/GameEnums'
import {Point} from '@colonist/game-mechanic-validators'
import {UIGamePlayerAvatarController} from './UIGamePlayerAvatarController'
import {UIReplayView} from '../ui_replay/elements/UIReplayView'
import {UIReplayController} from '../ui_replay/UIReplayController'
import {GameState, NestedReadonly} from '@colonist/game-states'
import {UIGameBeginnerModeController} from './ui_game_beginner_mode/UIGameBeginnerModeController'
import {UIGameBeginnerHintsController} from './ui_game_beginner_hints/UIGameBeginnerHintsController'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {ClientUserState} from '../ClientUserState'

export interface UIGameManagerMethods {
    settingsController: UIGameSettingsMethods
    canvasController: UIGameCanvasController
    gameController: UIGameController
    socketGameSend: SocketGameSend
    pauseController: UIGamePauseController
    replayView?: UIReplayView
    resetGameUIToRebuildOnReconnect: () => void
    gameState: GameClientState
    mapController: UIGameMapController
    playersController: UIGamePlayersController
    eventController: UIGameEventController
}

export abstract class UIGameManager implements UIGameManagerMethods {
    gameController: UIGameController

    gameState: GameClientState
    gameLayout: UIGameLayout
    socketGameSend: SocketGameSend

    eventController: UIGameEventController
    canvasController: UIGameCanvasController
    mapController: UIGameMapController

    replayView?: UIReplayView
    chatController?: UIGameFeedController
    bankController: UIGameBankController

    playerAvatarController: UIGamePlayerAvatarController
    playersController: UIGamePlayersController
    tradeController: UIGameTradeCreationController
    actionsController: UIGameActionsController
    diceController: UIGameDiceController
    toolTipController: UIGameToolTipController
    adController: UIAdController | undefined
    voteController: UIGameVoteController
    cardController: UIGameCardController
    timerController?: UIGameTimerController

    pauseController: UIGamePauseController
    settingsController: UIGameSettingsController

    originalMapPosition: {location: Point, zoom: Point} | undefined

    constructor(adController: UIAdController | undefined) {
        this.eventController = new UIGameEventController()
        this.playerAvatarController = new UIGamePlayerAvatarController(this)

        this.adController = adController
        this.eventController.registerUIGameMechanic(CA)
    }

    initializeSocket(gameState: GameClientState) {
        this.gameState = gameState
        this.socketGameSend = new SocketGameSend(socket.controller.sender, () => this.gameState.serverId)
    }

    resetGameUIToRebuildOnReconnect() {
        if(!this.canvasController.builtGame()) return
        const mapLocation = new Point(this.mapController.mapView.left, this.mapController.mapView.top)
        const mapZoom = new Point(this.mapController.mapView.scale.x, this.mapController.mapView.scale.y)
        this.originalMapPosition = {
            location: mapLocation,
            zoom: mapZoom,
        }
        this.canvasController.app.stage.removeChildren()
        this.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.RebuildingGameUI)
        this.eventController = new UIGameEventController()
        this.timerController?.stop()
    }

    abstract buildGame(configurations: BuildGameConfigurations): void
    abstract buildGameFeed(): void
    abstract buildReplayView(replayController: UIReplayController): void

    protected buildOptionalMechanics(gameState: NestedReadonly<GameState>) {
        if(this.gameController.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P) this.initBeginnerMode()

        const areBeginnerHintsEnabled = !gameState.mechanicBarbarianInvasionState && !gameState.mechanicPirateState
        if(gameState.mechanicSettlementState
                    && gameState.mechanicCityState
                    && gameState.mechanicRoadState
                    && areBeginnerHintsEnabled) this.initBeginnerHintsController()
    }

    private initBeginnerMode() {
        new UIGameBeginnerModeController(this)
        this.cardController.initBeginnerModeHelper()
        this.diceController.initBeginnerModeHelper()
    }

    private initBeginnerHintsController() {
        if(ClientUserState.userState != undefined && !ClientUserState.userState.isBeginner()) return
        if(UIUtilsEjsVariables.isUserOnMobileApp) return

        new UIGameBeginnerHintsController(this)
    }
}
